import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Check, MapPin, CreditCard, CalendarDays, Sparkles, ArrowRight, Building2, Smartphone } from "lucide-react";

const FEATURES = [
  { icon: CalendarDays, title: "Smart recurring jobs", desc: "Weekly, fortnightly, monthly, or custom — jobs auto-generate so your round runs itself." },
  { icon: MapPin, title: "Daily route + maps", desc: "Pin your day on a map, drag to reorder, tap to navigate. Built for vans, not desks." },
  { icon: CreditCard, title: "Instant paid invoices", desc: "Mark a job done, customer gets a branded Stripe link. Cash, card, Apple Pay, Google Pay." },
  { icon: Smartphone, title: "Mobile-first, PWA-ready", desc: "Install on your phone. Works on the ladder, works in the yard, works in the rain." },
  { icon: Building2, title: "Grows with your team", desc: "Assign jobs to staff, invite helpers, see what was completed — one tidy dashboard." },
  { icon: Sparkles, title: "Branded payments", desc: "Your logo, your colours, your message. Customers pay you, not a faceless portal." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7" data-testid="hero-section">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]" data-testid="hero-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                Built for UK window cleaners
              </span>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold">
                Run your round.
                <br />
                <span className="text-primary">Get paid faster.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-subtitle">
                WindowIQX is the clean, no-nonsense job & payments platform for window cleaning businesses.
                Recurring rounds, daily routes, branded invoices, and one-tap card payments — all in one place.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/signup" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-3 font-medium hover:bg-primary/90 active:scale-[0.98] transition" data-testid="hero-cta-signup">
                  Start 30-day free trial <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center rounded-md border border-border px-6 py-3 font-medium hover:bg-muted transition" data-testid="hero-cta-pricing">
                  See pricing
                </Link>
              </div>
              <div className="mt-6 text-xs text-muted-foreground" data-testid="hero-trust">
                No card needed · Cancel anytime · £ GBP · Plans from £10/mo
              </div>
            </div>
            <div className="md:col-span-5 relative" data-testid="hero-visual">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1598787169666-41bfa5319b69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBjbGVhbmVyJTIwc2t5fGVufDB8fHx8MTc3Nzg0MzQ3Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Window cleaner against blue sky"
                  className="h-full w-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 backdrop-blur p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Today's round</div>
                      <div className="mt-1 font-display text-xl font-bold">14 jobs · £420</div>
                    </div>
                    <div className="h-10 w-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-xs">
                      9/14
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block rounded-xl border border-border bg-card p-4 shadow-lg w-56">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Money today</div>
                <div className="mt-1 font-display text-2xl font-bold text-primary">£1,245.00</div>
                <div className="text-xs text-muted-foreground mt-1">from 28 customers</div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 -z-10 noise-bg"></div>
      </section>

      {/* Features bento */}
      <section id="features" className="bg-accent/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Features</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-bold">Everything your round needs. Nothing it doesn't.</h2>
            <p className="mt-4 text-muted-foreground text-lg">Designed with sole traders and small teams in mind — fast to learn, quick to use, works on your phone.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`rounded-xl border border-border bg-card p-6 hover:-translate-y-0.5 hover:shadow-sm transition ${
                  i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : i === 3 ? "md:col-span-7" : i === 4 ? "md:col-span-6" : "md:col-span-6"
                }`}
                data-testid={`feature-card-${i}`}
              >
                <f.icon className="h-6 w-6 text-primary" />
                <h3 className="mt-4 font-display text-xl tracking-tight font-bold">{f.title}</h3>
                <p className="mt-2 text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Simple pricing</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-bold">Start free. Grow when you're ready.</h2>
          </div>
          <Link to="/pricing" className="text-sm font-medium text-primary hover:underline" data-testid="see-full-pricing">Compare plans →</Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            { name: "Basic", price: "£10", tag: "For solo window cleaners", items: ["1 user", "Up to 100 customers", "Routes, jobs, invoices", "Stripe payments"] },
            { name: "Pro", price: "£20", featured: true, tag: "Most popular · small teams", items: ["Up to 3 users", "Unlimited customers", "Priority support", "All features"] },
            { name: "Team", price: "£35", tag: "Teams of 5+", items: ["5+ users", "Unlimited everything", "Admin controls", "White-glove onboarding"] },
          ].map((p) => (
            <div key={p.name} className={`rounded-2xl p-8 border ${p.featured ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`pricing-preview-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                {p.featured && <span className="text-xs font-bold uppercase tracking-wider text-primary">Popular</span>}
              </div>
              <div className="mt-4 font-display text-5xl tracking-tighter font-bold">{p.price}<span className="text-base text-muted-foreground font-medium">/mo</span></div>
              <div className="text-sm text-muted-foreground mt-1">{p.tag}</div>
              <ul className="mt-6 space-y-2 text-sm">
                {p.items.map((it) => <li key={it} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{it}</li>)}
              </ul>
              <Link to="/signup" className="mt-8 inline-flex w-full items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2.5 font-medium hover:bg-primary/90 transition" data-testid={`pricing-preview-cta-${p.name.toLowerCase()}`}>Start free trial</Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-accent/40">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-bold">Your round, running itself.</h2>
          <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">Join UK window cleaners who ditched paper diaries for a tool that actually works on the job.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/signup" className="inline-flex items-center rounded-md bg-primary text-primary-foreground px-6 py-3 font-medium" data-testid="bottom-cta-signup">Start your free trial</Link>
            <Link to="/login" className="inline-flex items-center rounded-md border border-border px-6 py-3 font-medium hover:bg-muted" data-testid="bottom-cta-login">Log in</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-muted-foreground flex justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} WindowIQX</div>
          <div className="flex gap-6">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
